// @ts-nocheck
import React from 'react';
import type { Modifier, Modifiers } from 'react-day-picker';

import { SingleDatePickerBody } from './SingleDatePickerBody';
import { SingleDatePickerNavbar } from './SingleDatePickerNavbar';
import { cn } from '@cobliteam/ui/utils';
import moment, { type Moment } from 'moment';

import type { DatePickerAlignment } from '../util/alignment';
import { animation } from '../util/animationStyle';
import { checkInputError } from '../util/inputValidator';
import { pickerContainer, singleNavbarWrapper } from '../util/styles';

export interface SingleDatePickerState {
  isDayPickerOpen: boolean;
  selectedDate?: Moment;
  hoveredDate?: Moment;
  inputValue: string;
  inputError: boolean;
  prevSelectedDateInMillis?: number;
}

export interface SingleDatePickerProps {
  handleDayChange?: (timestampInMillis: number) => void;
  selectedDateInMillis?: number;
  disabledDays?: Modifier | Modifier[];
  modifiers?: Partial<Modifiers>;
  fromMonth?: Date;
  toMonth?: Date;
  calendarFootnotes?: React.ReactNode;
  portal?: boolean;
  alignment?: DatePickerAlignment;
  disabled?: boolean;
  placeholder?: string;
  error?: boolean;
  errorMessage?: string;
  hideErrorMessage?: boolean;
  utcOffset?: number;
  name?: string;
  readOnly?: boolean;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class SingleDatePicker extends React.Component<SingleDatePickerProps, SingleDatePickerState> {
  private innerInput: HTMLElement | undefined = undefined;

  private divRef?: HTMLDivElement;

  constructor(props: SingleDatePickerProps) {
    super(props);

    let initialDefaultDate: Moment | undefined = moment.utc();

    const { utcOffset } = props;

    if (props.selectedDateInMillis) {
      const dateMoment = moment(props.selectedDateInMillis);
      const date = utcOffset ? dateMoment.utcOffset(utcOffset) : dateMoment;
      initialDefaultDate = date;
    } else if (this.props.placeholder) {
      initialDefaultDate = undefined;
    }

    this.state = {
      selectedDate: initialDefaultDate,
      inputValue: initialDefaultDate?.format('DD/MM/YYYY') || '',
      isDayPickerOpen: false,
      inputError: false,
      prevSelectedDateInMillis: props.selectedDateInMillis,
    };
  }

  private static getDerivedStateFromProps(nextProps: SingleDatePickerProps, prevState: SingleDatePickerState) {
    if (nextProps.selectedDateInMillis !== prevState.prevSelectedDateInMillis) {
      const utcSelectedDateInMillis = moment.utc(nextProps.selectedDateInMillis);
      return {
        inputValue: utcSelectedDateInMillis.format('DD/MM/YYYY'),
        selectedDate: utcSelectedDateInMillis,
        hoveredDate: utcSelectedDateInMillis,
        prevSelectedDateInMillis: nextProps.selectedDateInMillis,
      };
    }

    return {
      prevSelectedDateInMillis: nextProps.selectedDateInMillis,
    };
  }

  private handleOutsideClick = () => {
    /* Closed still in initial state */
    if (this.state.inputValue === '') {
      this.setState({ isDayPickerOpen: false, selectedDate: undefined });
      return;
    }

    const newDate = moment(this.state.inputValue, 'DD/MM/YYYY', true);

    const isDateValid = newDate.isValid();

    this.handleDayChange(newDate);

    /* Closed with invalid dates */
    if (!isDateValid) {
      this.setState({
        inputError: !isDateValid,
        inputValue: this.state.inputValue,
        isDayPickerOpen: false,
        selectedDate: undefined,
      });
      return;
    }

    this.setState({ isDayPickerOpen: false });
  };

  private setOuterInput = (outerInput: HTMLElement | undefined) => {
    this.outerInput = outerInput;
  };

  private setInnerInput = (innerInput: HTMLElement | undefined) => {
    this.innerInput = innerInput;
  };

  private handleDayChange = (newDate: Moment) => {
    const isDateValid = newDate.isValid();
    const isSameDate = Boolean(this.state.selectedDate) && newDate.isSame(this.state.selectedDate, 'day');

    if (!isDateValid || isSameDate) {
      return;
    }

    const newState: Partial<SingleDatePickerState> = {
      selectedDate: newDate,
      inputError: false,
      inputValue: newDate.format('DD/MM/YYYY'),
      isDayPickerOpen: false,
    };

    this.setState(
      (currentState) => ({ ...currentState, ...newState }),
      () => {
        const { selectedDate } = this.state;
        if (selectedDate && this.props.handleDayChange) {
          this.props.handleDayChange(selectedDate.valueOf());
        }
      }
    );
  };

  private handleFocus = () => {
    const { innerInput } = this;

    this.setState({ isDayPickerOpen: true }, () => {
      /* This timeout here is to wait for the changes in DOM, if you do it instantly the .focus() does not work. */
      this.timeoutHandler = window.setTimeout(() => innerInput?.focus(), 20);
    });
  };

  private handleInputChange = (inputValue: string) => {
    const newDate = moment(inputValue, 'DD/MM/YYYY', true);

    const partialState: Partial<SingleDatePickerState> = { inputValue };

    if (newDate.isValid()) {
      partialState.selectedDate = newDate;
      partialState.isDayPickerOpen = false;
      partialState.inputError = false;
    } else {
      partialState.inputError = checkInputError(inputValue);
      partialState.selectedDate = undefined;
    }

    this.setState(
      (currentState) => ({ ...currentState, ...partialState }),
      () => {
        const { selectedDate } = this.state;
        if (selectedDate && this.props.handleDayChange) {
          this.props.handleDayChange(selectedDate ? selectedDate.valueOf() : 0);
        }
      }
    );
  };

  private handleDayEnter = (day: Moment) => {
    this.setState({ hoveredDate: day });
  };

  private handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    // When it's mounted <SingleDatePickerBody/>, somehow it is not ready and mouseUp of event can trigger click away handler
    // So, we blocked this event to bubble because will hide <SingleDatePickerBody/> .
    if (this.state.isDayPickerOpen) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  public render = () => {
    const { alignment, disabled, disabledDays, portal, placeholder, error, errorMessage, hideErrorMessage, name, modifiers, readOnly, fromMonth, toMonth, calendarFootnotes } =
      this.props;
    const { isDayPickerOpen, selectedDate, hoveredDate, inputValue, inputError } = this.state;
    const { calendar, hideCalendar, singleCalendar, navbar, hideNavbar } = animation(alignment, disabled);

    return (
      <div className={pickerContainer()} ref={(ref: HTMLDivElement) => (this.divRef = ref)}>
        <div className={cn(singleNavbarWrapper, navbar, isDayPickerOpen ? hideNavbar : '')} onMouseUp={this.handleMouseUp}>
          <SingleDatePickerNavbar
            inputError={inputError}
            inputValue={inputValue}
            inputRef={this.setOuterInput}
            handleInputChange={() => {}}
            handleFocusInput={this.handleFocus}
            disabled={disabled}
            noFocus
            placeholder={placeholder}
            error={error}
            errorMessage={errorMessage}
            name={name}
            hideErrorMessage={hideErrorMessage}
          />
        </div>
        <div className={cn(calendar, singleCalendar, isDayPickerOpen ? '' : hideCalendar)}>
          <SingleDatePickerBody
            handleDayChange={this.handleDayChange}
            setInnerInput={this.setInnerInput}
            selectedDate={selectedDate}
            hoveredDate={hoveredDate}
            outsideDivRef={this.divRef}
            isDayPickerOpen={isDayPickerOpen}
            inputError={inputError}
            inputValue={inputValue}
            handleInputChange={this.handleInputChange}
            handleDayMouseEnter={this.handleDayEnter}
            disabledDays={disabledDays}
            modifiers={modifiers}
            fromMonth={fromMonth}
            toMonth={toMonth}
            calendarFootnotes={calendarFootnotes}
            portal={portal}
            hideCalendarClass={hideCalendar}
            onClickAway={this.handleOutsideClick}
            alignment={alignment}
            error={error}
            errorMessage={errorMessage}
            readOnly={readOnly}
          />
        </div>
      </div>
    );
  };
}
